import PropTypes from 'prop-types'
import smoothscroll from 'smoothscroll-polyfill'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'
import React, { Suspense, useEffect, useRef, useState } from 'react'

import Icon from '../icons/Icon'
import Tooltip from '../tooltip/Tooltip'
import { getFormatedPrice } from './utils'
import Card from '../card/Card'
import { useTranslation } from '../../utils/useTranslation'

smoothscroll.polyfill()

const Wrapper = styled.div`
  min-height: ${p => p.height || 0};
  max-width: 100vw;
  display: flex;
  overflow-x: auto;
  overflow-y: visible;
  @media (max-width: 601px) {
    scroll-snap-type: x mandatory;
  }
`
const P = styled.p`
  display: flex;
  align-items: center;
  padding: 0 ${prop('theme.body.padding')};
  text-align: left;
  font-family: ${prop('theme.font.preamble.fontFamily')};
  font-weight: ${prop('theme.font.preamble.fontWeight')};
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 4px 0 18px ${prop('theme.body.marginDesktop')};
  }

  span {
    @media (min-width: ${prop('theme.body.tabletWidth')}) {
      font-size: 18px;
      line-height: 26px;
    }
  }
`
const Dots = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  z-index: 1;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: none;
  }
`
const Dot = styled.div`
  background-color: ${theme('colors.primary')};
  opacity: ${p => (p.focused ? 1 : 0.3)};
  width: 12px;
  height: 12px;
  margin: 3px;
  border-radius: 50%;
  :hover {
    cursor: pointer;
    opacity: ${p => (p.focused ? 1 : 0.7)};
  }
`
const Space = styled.div`
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 15px 40px;
  }
`
const SmallIcon = styled.span`
  width: 20px;
  margin-left: 10px;
  cursor: pointer;
`
const Arrow = styled.button`
  display: none;
  position: absolute;
  left: ${p => (p.right ? 'auto' : '70px')};
  right: ${p => (p.right ? '70px' : 'auto')};
  top: 25%;
  width: 50px;
  height: 50px;
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.section.bgColor};
  border-radius: 50%;
  font-size: 25px;
  z-index: 8;
  user-select: none;
  cursor: pointer;
  :hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  }
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: flex;
  }
`
const Container = styled.div`
  position: relative;
  padding-top: 20px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    padding-top: 0;
  }
`
const Content = styled.div`
  background-color: ${p => p.theme.section.bgColor};
  border: 1px solid ${p => p.theme.section.bgColor};
  padding-bottom: 20px;

  ${p =>
    p.extraHeight &&
    `
    @media(max-width:${prop('theme.body.tabletWidth')}) {
      min-height: 500px;
    }
  `}
`

const getFocusedDot = (x, cardWidth, length) => {
  const diff = 0.6
  /* eslint-disable-next-line no-plusplus */
  for (let i = 0; i < length; i++) {
    if (x > cardWidth * (diff + i) && x < cardWidth * (diff + 1 + i)) {
      return i + 1
    }
  }
  return 0
}

const getScrollRight = ref =>
  ref.scrollWidth - (ref.scrollLeft + ref.clientWidth) + 1

const getHeight = id => {
  if (id === 'range') return '380px'
  if (id === 'units') return '350px'
  if (id === 'worktops') return '450px'
  if (id === 'appliances') return '260px'
  return '200px'
}
const getModulator = id => {
  if (id === 'range') return 15
  if (id === 'units') return -10
  if (id === 'worktops') return -5
  return 0
}

const Cards = ({
  id,
  type,
  description,
  cards,
  onSelect,
  selection,
  isOpen,
  tooltip,
  toggleTooltip,
  showTooltip,
}) => {
  const { t, country } = useTranslation()
  const sideMargin = 100
  const [focusedDot, setFocusedDot] = useState(0)
  const [x, setX] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const [scrollRightAvailable, setScrollRightAvailable] = useState(false)
  const [scrollLeftAvailable, setScrollLeftAvailable] = useState(false)

  const cardsRef = useRef(null);

  const handleScroll = container => {
    const position = container.scrollLeft
    setX(position)
  }

  const scrollTo = (position, length) => {
    const positionRatio = position / (length - 1)
    const shift = 70 * positionRatio
    cardsRef.current.scroll({
      top: 0,
      left: cardWidth * position + shift,
      behavior: 'smooth',
    })
  }

  const scrollBy = left => {
    cardsRef.current.scrollBy({
      top: 0,
      left: left ? cardWidth : -cardWidth,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (!cards) return
    const width = 300
    setCardWidth(width + getModulator(id))
    cardsRef.current.addEventListener('scroll', () =>
      handleScroll(cardsRef.current)
    )

    setScrollLeftAvailable(cardsRef.current.scrollLeft > sideMargin)
    setScrollRightAvailable(getScrollRight(cardsRef.current) > sideMargin)
    /* eslint-disable-next-line consistent-return */
    return () => {
      cardsRef.current.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (!cards) return
    setScrollLeftAvailable(cardsRef.current.scrollLeft > sideMargin)
    setScrollRightAvailable(getScrollRight(cardsRef.current) > sideMargin)
    setFocusedDot(getFocusedDot(x, cardWidth, cards.length))
  }, [x])

  useEffect(() => {
    if (!isOpen) return
    const selectedIndex = cards.findIndex(
      card => card.title === selection[id].title
    )
    scrollTo(selectedIndex, cards.length)
  }, [isOpen])

  return (
    <Container>
      <Content extraHeight={showTooltip && id === 'installation'}>
        {scrollLeftAvailable && (
          <Arrow onClick={() => scrollBy(false)}>&larr;</Arrow>
        )}
        {scrollRightAvailable && (
          <Arrow onClick={() => scrollBy(true)} right>
            &rarr;
          </Arrow>
        )}
        {description && (
          <P>
            <span>{description}</span>
            {tooltip && (
              <SmallIcon onClick={() => toggleTooltip(true)}>
                <Icon name="info" fill="#192228" />
              </SmallIcon>
            )}
          </P>
        )}

        {cards && (
          <Wrapper id={id} ref={cardsRef} height={getHeight(id)}>
            <Space>&nbsp;</Space>
            {cards.map(card => (
              <Suspense key={card.title} fallback={<></>}>
                <Card
                  type={type}
                  section={type}
                  {...card}
                  selection={selection}
                  priceText={getFormatedPrice({
                    card,
                    selection,
                    startingText: t('app.components.card.from'),
                    country,
                  })}
                  onSelect={onSelect}
                />
              </Suspense>
            ))}
            <Space>&nbsp;</Space>
          </Wrapper>
        )}
        {cards?.length > 1 && (
          <Dots>
            {cards.map((c, i) => (
              <Dot
                key={c.id + c.title}
                focused={focusedDot === i}
                onClick={() => scrollTo(i, cards.length)}
              />
            ))}
          </Dots>
        )}
      </Content>
      {showTooltip && (
        <Tooltip id={id} {...tooltip} onClose={() => toggleTooltip(false)} />
      )}
    </Container>
  )
}

Cards.defaultProps = {
  cards: [],
}

Cards.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selection: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  tooltip: PropTypes.shape({}).isRequired,
  toggleTooltip: PropTypes.func.isRequired,
  showTooltip: PropTypes.bool.isRequired,
}

export default Cards
